import React, { useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { LoadingScreen } from '@/components/LoadingScreen'
import { getTeamsAuthToken } from '@/lib/teamsHost'
import type { HostKind } from '@/lib/teamsHost'

/**
 * Web: unchanged — a full-page `loginRedirect` is the correct interactive
 * flow in a normal browser tab, and the MSAL account gates the app.
 *
 * Teams: `loginRedirect` is fundamentally broken inside a Teams tab iframe —
 * `login.microsoftonline.com` sets `X-Frame-Options: DENY` and refuses to be
 * framed at all. MSAL is not used in this host either: Nested App
 * Authentication was tried first and failed against real Teams clients
 * (web: invalid_client, desktop: ApiContractViolation). Instead we use
 * Teams classic SSO — `authentication.getAuthToken()` — where the Teams
 * host itself issues a token for the API app (see teamsHost.ts). One
 * successful token proves the session; every API call then requests a
 * fresh token through the same channel (useApiClient.ts). If it fails,
 * we surface the code plainly rather than attempting a redirect that
 * cannot work in this host.
 */

const TeamsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const attemptedRef = useRef(false)
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending')
  const [errorCode, setErrorCode] = useState<string | null>(null)

  useEffect(() => {
    if (attemptedRef.current) return
    attemptedRef.current = true

    getTeamsAuthToken()
      .then(() => setState('ok'))
      .catch((error) => {
        console.error('[SCC][Teams] getAuthToken failed:', error)
        setErrorCode(error?.errorCode || error?.message || 'unknown_error')
        setState('error')
      })
  }, [])

  if (state === 'error') {
    return (
      <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#1B2733' }}>
        <strong>No se pudo iniciar sesión dentro de Teams.</strong>
        <p style={{ color: '#6C7889' }}>Código: {errorCode}</p>
      </div>
    )
  }
  if (state === 'pending') return <LoadingScreen />
  return <>{children}</>
}

const WebGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal()
  const attemptedRef = useRef(false)

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return
    if (accounts.length > 0) return
    if (attemptedRef.current) return
    attemptedRef.current = true

    instance.loginRedirect({ scopes: ['User.Read'] }).catch(console.error)
  }, [inProgress, accounts, instance])

  if (inProgress !== InteractionStatus.None || accounts.length === 0) {
    return <LoadingScreen />
  }
  return <>{children}</>
}

export const AuthGate: React.FC<{ host: HostKind; children: React.ReactNode }> = ({ host, children }) => {
  if (host === 'teams') return <TeamsGate>{children}</TeamsGate>
  return <WebGate>{children}</WebGate>
}
